import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../store/Store";
import { FetchDataFailureAction, FetchDataSuccessAction, fetchData, patchData } from "../../store/actions";
import { getReportingManager, setReportingManager, viewUsers } from "../../Service/Apis";
import { ToastVariant, useCustomToast } from "../../components/Methods/SnackBar";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface UserRow {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  roles: string[];
  reporting_manager_name?: string | null;
}

const ManageAssignments: React.FC = () => {
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState<{ open: boolean; userId?: string }>(
    { open: false }
  );
  const showToast = useCustomToast();
  const navigate = useNavigate();

  const loadData = async () => {
    setLoading(true);
    try {
      const users: UserRow[] = await dispatch(fetchData(viewUsers));
      // Fetch reporting manager for each user in parallel (can be optimized with a bulk API later)
      const withManagers = await Promise.all(
        users.map(async (u) => {
          try {
            const rm = await dispatch(fetchData(`${getReportingManager}?employee_id=${u.id}`));
            return { ...u, reporting_manager_name: rm?.reporting_manager_name || null };
          } catch {
            return { ...u, reporting_manager_name: null };
          }
        })
      );
      setRows(withManagers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const columns: GridColDef[] = [
    {
      field: "sr",
      headerName: "Sr No.",
      valueGetter: (params) => rows.findIndex((r) => r.id === (params as any).row.id) + 1,
      flex: 0.4,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: "Employee",
      valueGetter: (params) => `${(params as any).row.first_name} ${(params as any).row.last_name}`,
      flex: 1.2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "employee_code",
      headerName: "Emp Code",
      flex: 0.8,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "reporting_manager_name",
      headerName: "Reporting Manager",
      flex: 1.2,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => ((params as any).row.reporting_manager_name || "Unassigned"),
    },
    {
      field: "actions",
      headerName: "Delete",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button size="small" onClick={() => setConfirmOpen({ open: true, userId: params.row.id })}>
          DELETE
        </Button>
      ),
    },
  ];

  const clearManager = async () => {
    if (!confirmOpen.userId) return;
    try {
      await dispatch(
        patchData(setReportingManager, {
          employee_id: confirmOpen.userId,
          manager_id: null,
        } as any)
      );
      showToast("Cleared reporting manager", "success" as ToastVariant, true);
      setConfirmOpen({ open: false });
      loadData();
    } catch {
      setConfirmOpen({ open: false });
    }
  };

  const handleDownload = () => {
    const data = rows.map((r, idx) => ({
      "Sr No.": idx + 1,
      "Emp Code": r.employee_code,
      "Employee": `${r.first_name} ${r.last_name}`,
      "Reporting Manager": r.reporting_manager_name || "Unassigned",
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Assignments");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "manager-assignments.xlsx");
  };

  return (
    <Box sx={{ mt: 12, mb: 10 }}>
      <Box sx={{ m: 2, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", m: 1, p: 1 }}>
          <Typography variant="h4" fontWeight="bold">Employee Manager Details</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", m: 1, p: 1 }}>
          <Button variant="contained" size="small" onClick={() => navigate("/assign-manager/form")}>
            ASSIGN MANAGER
          </Button>
          <Button variant="outlined" size="small" onClick={handleDownload} sx={{ ml: 1 }}>
            DOWNLOAD
          </Button>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", m: 1, p: 1 }}>
          <Paper sx={{ width: "90%" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              disableColumnMenu
              slots={{ toolbar: GridToolbar }}
              slotProps={{ toolbar: { showQuickFilter: true, csvOptions: { disableToolbarButton: true }, printOptions: { disableToolbarButton: true } } }}
              pageSizeOptions={[5, 10, 15, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            />
          </Paper>
        </Box>
      </Box>

      <Dialog open={confirmOpen.open} onClose={() => setConfirmOpen({ open: false })}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Clear this employee's reporting manager?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen({ open: false })}>No</Button>
          <Button variant="contained" onClick={clearManager}>Yes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageAssignments;


